/**
 * Words the guide search drops from the index and the query, per locale
 * (#2108): articles, pronouns, question words, auxiliaries, common
 * prepositions and, in German, separable verb prefixes ("Wie füge ich ein
 * Kind hinzu"). Content words stay out: "not", "new" or "nicht" change what a
 * reader looks for. At most about 60 per locale; written as people type them,
 * matched after `normalizeText`.
 */
const STOPWORDS: Record<string, readonly string[]> = {
  en: [
    'a', 'an', 'the', 'this', 'that', 'these', 'those', 'i', 'me', 'my', 'we', 'our', 'you', 'your', 'it', 'its',
    'he', 'she', 'they', 'their', 'them', 'how', 'what', 'when', 'where', 'why', 'who', 'which', 'do', 'does', 'did',
    'is', 'are', 'was', 'be', 'been', 'can', 'could', 'should', 'would', 'will', 'must', 'to', 'of', 'in', 'on', 'at',
    'for', 'with', 'from', 'by', 'and', 'or', 'if', 'there', 'as', 'about', 'into', 'so',
  ],
  de: [
    'der', 'die', 'das', 'den', 'dem', 'des', 'ein', 'eine', 'einen', 'einem', 'einer', 'eines', 'ich', 'sie', 'es',
    'wir', 'mich', 'mir', 'man', 'mein', 'meine', 'wie', 'was', 'wo', 'wann', 'warum', 'wer', 'welche', 'welcher',
    'kann', 'können', 'muss', 'müssen', 'soll', 'will', 'ist', 'sind', 'wird', 'werden', 'hat', 'haben', 'und',
    'oder', 'in', 'im', 'an', 'am', 'auf', 'aus', 'bei', 'mit', 'von', 'zu', 'zum', 'zur', 'für', 'hinzu', 'ab',
    'zurück', 'fest',
  ],
  fr: [
    'le', 'la', 'les', 'l', 'un', 'une', 'des', 'du', 'de', 'd', 'je', 'j', 'me', 'm', 'on', 'nous', 'vous', 'il',
    'elle', 'elles', 'mon', 'ma', 'mes', 'ce', 'cet', 'cette', 'ces', 'comment', 'quoi', 'que', 'qu', 'quel',
    'quelle', 'quels', 'quelles', 'quand', 'pourquoi', 'qui', 'est', 'sont', 'peut', 'peux', 'puis', 'dois', 'doit',
    'faire', 'et', 'ou', 'à', 'au', 'aux', 'en', 'dans', 'sur', 'pour', 'par', 'avec', 'se', 's', 'y',
  ],
  it: [
    'il', 'lo', 'la', 'i', 'gli', 'le', 'l', 'un', 'uno', 'una', 'di', 'del', 'dello', 'della', 'dei', 'degli',
    'delle', 'da', 'dal', 'dalla', 'in', 'nel', 'nella', 'a', 'al', 'alla', 'ai', 'su', 'per', 'con', 'come', 'cosa',
    'che', 'quando', 'dove', 'perché', 'chi', 'quale', 'quali', 'io', 'mi', 'si', 'ci', 'lei', 'sono', 'posso', 'può',
    'devo', 'deve', 'e', 'o', 'mio', 'mia', 'suo', 'sua', 'questo', 'questa',
  ],
  es: [
    'el', 'la', 'los', 'las', 'un', 'una', 'unos', 'unas', 'lo', 'de', 'del', 'a', 'al', 'en', 'con', 'por', 'para',
    'como', 'que', 'cuando', 'donde', 'quien', 'cual', 'yo', 'me', 'se', 'le', 'les', 'mi', 'mis', 'su', 'sus',
    'usted', 'es', 'son', 'hay', 'puedo', 'puede', 'debo', 'debe', 'hago', 'hacer', 'y', 'o', 'este', 'esta',
  ],
  pt: [
    'o', 'a', 'os', 'as', 'um', 'uma', 'uns', 'umas', 'de', 'do', 'da', 'dos', 'das', 'em', 'no', 'na', 'nos', 'nas',
    'ao', 'aos', 'com', 'por', 'pelo', 'pela', 'para', 'como', 'que', 'quando', 'onde', 'porque', 'quem', 'qual',
    'eu', 'me', 'se', 'lhe', 'meu', 'minha', 'seu', 'sua', 'são', 'posso', 'pode', 'devo', 'deve', 'faço', 'fazer',
    'e', 'ou', 'este', 'esta',
  ],
};

const NO_STOPWORDS: ReadonlySet<string> = new Set();
const SETS = new Map<string, ReadonlySet<string>>();

/** The stop words of `locale`, normalized with `normalize`; none for an unknown locale. */
export function stopwords(locale: string | undefined, normalize: (word: string) => string): ReadonlySet<string> {
  const words = locale ? STOPWORDS[locale] : undefined;
  if (!locale || !words) return NO_STOPWORDS;
  let set = SETS.get(locale);
  if (!set) {
    set = new Set(words.map(normalize));
    SETS.set(locale, set);
  }
  return set;
}
